import { useCallback } from 'react';

import { CircleHelpIcon } from 'lucide-react';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { GraphType } from '@/modules/analytics/components/fieldsets/AnalyticsFieldset/AnalyticsFieldset';
import useExport from '@/modules/analytics/hooks/useExport';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

type Props = {
  children?: React.ReactNode;
  title: string;
  tooltip?: string;
  learnMoreUrl?: string;
  data?: unknown;
  hasExport?: boolean;
  graphType?: GraphType;
};

const AnalyticsTitle = ({ title, tooltip, learnMoreUrl, data, hasExport, graphType }: Props) => {
  const { onExportClick, isExporting } = useExport();

  const handleExport = useCallback(
    (format: ExportFormat) => {
      trackEvent(EventName.RunAnalyticsExport, { title: title || 'Usage', type: format });
      onExportClick(data, title, format, graphType);
    },
    [data, onExportClick, title, graphType]
  );

  return (
    <div className="flex items-center text-foreground-muted">
      {title && <span className="text-base font-medium text-foreground">{title}</span>}
      {tooltip && (
        <InfoTooltip content={tooltip} learnMoreUrl={learnMoreUrl} side="right">
          <CircleHelpIcon size={12} className="text-foreground-muted" />
        </InfoTooltip>
      )}
      {hasExport && (
        <div className="ml-1">
          <ExportButton
            variant="icon"
            onExport={handleExport}
            isExporting={isExporting}
            disabled={!data}
          />
        </div>
      )}
    </div>
  );
};

export default AnalyticsTitle;
